import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import CreateBatchModal from './components/CreateBatchModal';
import AddMembersModal from './components/AddMembersModal';
import {
  getBatchesByManager, getBatchById, deleteBatch, removeTraineesFromBatch
} from '../../api_service';

const BatchManagement = () => {
  const navigate = useNavigate();
  const managerId = sessionStorage.getItem('userId');

  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadBatches(); }, []);

  const loadBatches = async () => {
    setLoading(true);
    try {
      const res = await getBatchesByManager(managerId);
      const list = Array.isArray(res?.data) ? res.data : [];
      setBatches(list);
      setSelectedBatch(prev =>
        prev ? list.find(b => b.batchId === prev.batchId) || null : null
      );
    } catch {
      setBatches([]);
    } finally {
      setLoading(false);
    }
  };

  const refreshSelected = async () => {
    if (!selectedBatch) return;
    try {
      const res = await getBatchById(selectedBatch.batchId);
      const updated = res?.data || null;
      setSelectedBatch(updated);
    } catch {
      // ignore
    }
    await loadBatches();
  };

  const handleSelect = (batch) => {
    setSelectedBatch(batch);
  };

  const handleDelete = async (e, batchId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this batch? This cannot be undone.')) return;
    try {
      await deleteBatch(batchId);
      if (selectedBatch?.batchId === batchId) setSelectedBatch(null);
      await loadBatches();
    } catch {
      // ignore
    }
  };

  const handleRemoveMember = async (member) => {
    if (!selectedBatch) return;
    try {
      await removeTraineesFromBatch(selectedBatch.batchId, { traineeIds: [member.userid] });
      await refreshSelected();
    } catch {
      // ignore
    }
  };

  const totalBatches = batches.length;
  const totalMemberships = batches.reduce((s, b) => s + (b.members?.length || 0), 0);
  const avgBatchSize = totalBatches > 0 ? Math.round(totalMemberships / totalBatches) : 0;

  return (
    <div className="min-h-screen bg-background">
      <Header
        userName={sessionStorage.getItem('userName') || 'User'}
        userRole="manager"
        onLogout={() => navigate('/')}
      />
      <main className="pt-16 max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Batches</h1>
            <p className="text-sm text-muted-foreground mt-1">Group trainees into batches and assign work to the whole group</p>
          </div>
          <Button onClick={() => setShowCreate(true)} iconName="Plus" iconSize={16}>
            Create Batch
          </Button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {[
            { label: 'Total Batches', value: totalBatches, icon: 'Layers', color: 'text-primary' },
            { label: 'Memberships', value: totalMemberships, icon: 'Users', color: 'text-accent' },
            { label: 'Avg Batch Size', value: avgBatchSize, icon: 'BarChart3', color: 'text-success' },
          ].map(card => (
            <div key={card.label} className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  <Icon name={card.icon} size={18} className={card.color} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{card.value}</p>
                  <p className="text-xs text-muted-foreground">{card.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Batch list */}
          <div className="lg:col-span-2 bg-card border border-border rounded-xl overflow-hidden">
            <div className="p-4 border-b border-border">
              <h2 className="font-semibold text-foreground">Batches ({batches.length})</h2>
            </div>
            {loading ? (
              <div className="p-8 text-center text-muted-foreground text-sm">Loading...</div>
            ) : batches.length === 0 ? (
              <div className="p-8 text-center">
                <Icon name="Layers" size={40} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No batches yet. Create your first batch.</p>
              </div>
            ) : (
              <div className="divide-y divide-border overflow-y-auto max-h-[500px]">
                {batches.map(batch => {
                  const isSelected = selectedBatch?.batchId === batch.batchId;
                  return (
                    <div
                      key={batch.batchId}
                      onClick={() => handleSelect(batch)}
                      className={`p-4 cursor-pointer hover:bg-muted/50 transition-colors ${isSelected ? 'bg-primary/5 border-l-2 border-primary' : ''}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-foreground line-clamp-2">{batch.name}</p>
                        <button
                          onClick={(e) => handleDelete(e, batch.batchId)}
                          className="text-muted-foreground hover:text-error transition-colors shrink-0"
                          title="Delete batch"
                        >
                          <Icon name="Trash2" size={16} />
                        </button>
                      </div>
                      {batch.description && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{batch.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                          {batch.members?.length || 0} {(batch.members?.length || 0) === 1 ? 'member' : 'members'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Batch detail panel */}
          <div className="lg:col-span-3 bg-card border border-border rounded-xl overflow-hidden">
            {!selectedBatch ? (
              <div className="h-full flex flex-col items-center justify-center p-12 text-center">
                <Icon name="MousePointerClick" size={40} className="text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground">Select a batch to view its members</p>
              </div>
            ) : (
              <>
                <div className="p-4 border-b border-border flex items-start justify-between gap-3">
                  <div>
                    <h2 className="font-semibold text-foreground">{selectedBatch.name}</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {selectedBatch.description || 'No description'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {selectedBatch.members?.length || 0} members
                    </p>
                  </div>
                  <Button size="sm" onClick={() => setShowAddMembers(true)} iconName="UserPlus" iconSize={14}>
                    Add Trainees
                  </Button>
                </div>
                <div className="divide-y divide-border overflow-y-auto max-h-[460px]">
                  {(selectedBatch.members || []).length === 0 ? (
                    <div className="p-8 text-center text-sm text-muted-foreground">No members in this batch yet</div>
                  ) : (
                    selectedBatch.members.map(member => (
                      <div key={member.userid} className="p-4 flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {member.firstname} {member.lastname}
                          </p>
                          <p className="text-xs text-muted-foreground">{member.emailid}</p>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => handleRemoveMember(member)}>
                          Remove
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      {showCreate && (
        <CreateBatchModal
          managerId={managerId}
          onClose={() => setShowCreate(false)}
          onCreated={loadBatches}
        />
      )}

      {showAddMembers && selectedBatch && (
        <AddMembersModal
          managerId={managerId}
          batch={selectedBatch}
          onClose={() => setShowAddMembers(false)}
          onUpdated={refreshSelected}
        />
      )}
    </div>
  );
};

export default BatchManagement;
